import { FC, ReactNode } from 'react';

import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

interface OptionProps {
  label: string | ReactNode;
  isTitle?: boolean;
  value: string;
}

interface SelectProps
  extends Omit<MuiSelectProps<string | Array<string>>, 'onChange'> {
  id?: string;
  label?: string | ReactNode;
  helperText?: string | boolean;
  displaySubHeaders?: boolean;
  options: Array<OptionProps>;
  multiple?: boolean;
  value: string | Array<string>;
  onChange?: (value: string | Array<string>) => void;
  renderOptionLabel?: (label: string | ReactNode) => ReactNode;
}

export const Select: FC<SelectProps> = ({
  id = 'select-mui',
  disabled,
  displaySubHeaders,
  error,
  onChange,
  options,
  multiple,
  value: selectedValue,
  size,
  variant,
  helperText,
  label,
  renderOptionLabel,
  ...props
}) => {
  const renderValue = (selected: string | Array<string>) => {
    let renderedValue = '';
    options.forEach(({ value, label: optionLabel }) => {
      if (multiple) {
        if ((selected as string[]).indexOf(value) > -1) {
          renderedValue += `${renderedValue === '' ? '' : ', '}${
            optionLabel as string
          }`;
        }
      } else if (value === selected) {
        renderedValue = optionLabel as string;
      }
    });
    return renderedValue;
  };

  const defaultRenderOptionLabel = (optionLabel: string | ReactNode) => {
    if (multiple) {
      return <ListItemText primary={optionLabel} />;
    }
    return optionLabel;
  };

  return (
    <FormControl
      variant={variant}
      size={size}
      error={error}
      disabled={disabled}
    >
      {label && <InputLabel id={id}>{label}</InputLabel>}
      <MuiSelect
        labelId={id}
        label={label}
        value={selectedValue}
        multiple={multiple}
        onChange={(event: SelectChangeEvent<typeof selectedValue>) => {
          const {
            target: { value },
          } = event;
          if (onChange) {
            onChange(value);
          }
        }}
        renderValue={renderValue}
        {...props}
      >
        {options.map(({ label: optionLabel, value, isTitle }) => {
          if (displaySubHeaders && isTitle) {
            return (
              <ListSubheader key={`title-${value}`}>
                {optionLabel}
              </ListSubheader>
            );
          }
          if (!isTitle) {
            if (multiple) {
              return (
                <MenuItem key={value} value={value}>
                  <Checkbox
                    checked={(selectedValue as string[]).indexOf(value) > -1}
                  />
                  {renderOptionLabel
                    ? renderOptionLabel(optionLabel)
                    : defaultRenderOptionLabel(optionLabel)}
                </MenuItem>
              );
            }
            return (
              <MenuItem key={value} value={value}>
                {renderOptionLabel
                  ? renderOptionLabel(optionLabel)
                  : defaultRenderOptionLabel(optionLabel)}
              </MenuItem>
            );
          }
          return null;
        })}
      </MuiSelect>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
